import React, { useEffect, useState } from 'react';
import { Layout, Menu, Typography, Table, Tag, Card } from 'antd';
import {
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import AccountInfo from './AccountInfoPage';
import UpdateSearchPage from './UpdateSearchPage';
import { getUser, redirectIfNotLogin } from '../functions/userFuncs';
import Header from '../components/Header';
import axios from 'axios';
import { signOut } from 'aws-amplify/auth';

const { Title } = Typography;
const { Content, Sider } = Layout;

interface UserInfo {
  name: string;
  email: string;
}

interface LostItem {
  category: string;
  brand: string;
  model: string;
  color: string;
  _report?: {
    planExpiry: string | null;
    _selectedPlan?: {
      title: string;
      durationInMonths: number;
    };  };

}

const StatusPage: React.FC<{
  isLogged: boolean;
  logUser: (value: boolean) => void;
}> = ({ isLogged, logUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const paymentStatus = localStorage.getItem('paymentStatus');

  const [userInfo, setUserInfo] = useState<UserInfo>({ name: '', email: '' });
  const [lostItems, setLostItems] = useState<LostItem[]>([]);
  const [collapsed, setCollapsed] = useState(false);

  const getSelectedKey = () => {
    if (location.pathname.includes('/status/account')) return 'account';
    if (location.pathname.includes('/status/modify-search'))
      return 'modify-search';
    return 'status';
  };

  useEffect(() => {
    redirectIfNotLogin(navigate);
    getUser()
      .then(user => {
        if (user) {
          setUserInfo(user.attributes);
          return axios.get(
            `${process.env.REACT_APP_INSTANCE}/api/report/${user?.attributes?.email}`
          );
        }
      })
      .then(res => {
        if (res) setLostItems(res.data.items);
      });
  }, [navigate]);

  async function logOut() {
    try {
      await signOut();
      logUser(false);
      localStorage.clear();
      navigate('/');
    } catch (e) { }
  }

  const handleMenuClick = () => {
    if (window.innerWidth <= 768) {
      setCollapsed(true);
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', marginTop: '60px' }}>
      <style>
        {`
          .table-desktop {
            display: block;
          }

          .table-mobile {
            display: none;
          }

          @media screen and (max-width: 768px) {
            .table-desktop {
              display: none;
            }

            .table-mobile {
              display: block;
            }
          }
        `}
      </style>
      <Header />
      <Layout>
        <Sider
          breakpoint='lg'
          collapsedWidth='0'
          collapsible
          collapsed={collapsed}
          onCollapse={collapsed => setCollapsed(collapsed)}
          style={{
            background: '#fff',
            position: 'fixed',
            left: 0,
            top: '60px',
            bottom: 0,
            borderRight: '1px solid #e0e0e0', // Light grey left border
          }}
          trigger={
            collapsed ? (
              <div style={{ background: '#000', padding: '0 10px' }}>
                <span style={{ color: '#fff' }}>☰</span>
              </div>
            ) : null
          }
        >
          <Menu
            theme='light'
            mode='inline'
            selectedKeys={[getSelectedKey()]}
            onClick={handleMenuClick} // Collapse the menu when an item is clicked
          >
            <Menu.Item key='status'>
              <Link to='/'>Status</Link>
            </Menu.Item>
            <Menu.Item key='account'>
              <Link to='/status/account'>Account</Link>
            </Menu.Item>
            <Menu.Item key='modify-search'>
              <Link to='/status/modify-search'>Modify Search</Link>
            </Menu.Item>
            <Menu.Item
              key='logout'
              style={{ position: 'absolute', bottom: 0, width: '100%' }}
              onClick={logOut}
            >
              Log Out
            </Menu.Item>
          </Menu>
        </Sider>
        <Layout
          style={{
            marginLeft: collapsed ? 0 : 200,
            transition: 'margin-left 0.2s',
          }}
        >
          <Content style={{ margin: '24px 16px 0' }}>
            <div style={{ padding: 24, minHeight: 280 }}>
              <Routes>
                <Route
                  path='/'
                  element={
                    <Status
                      paymentStatus={paymentStatus}
                      lostItems={lostItems}
                    />
                  }
                />
                <Route path='account' element={<AccountInfo />} />
                <Route path='modify-search' element={<UpdateSearchPage />} />
              </Routes>
            </div>
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

const Status: React.FC<{
  paymentStatus: string | null;
  lostItems: LostItem[];
}> = ({ paymentStatus, lostItems }) => {
  console.log('lostItems:', lostItems);
  const columns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      // render: (category: string) => <a>{category}</a>,
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
    },
    {
      title: 'Payment Status',
      dataIndex: '_report',
      key: '_report',
      render: (report: any) => {
        const planExpired = new Date(report.planExpiry) <= new Date();
        return (
          <Tag
            color={
              !report.planExpiry ? 'red' : planExpired ? 'orange' : 'green'
            }
          >
            {!report.planExpiry ? 'Unpaid' : planExpired ? 'Expired' : 'Paid'}
          </Tag>
        );
      },
    },
    {
      title: 'Selected Plan',
      dataIndex: '_report',
      key: '_report',
      render: (report: any) => (
        <span>
          {report._selectedPlan
            ? `${report._selectedPlan.title} (${report._selectedPlan?.durationInMonths} months)`
            : ''}
        </span>
      ),
    },
  ];

  return (
    <div>
      <h1>Status</h1>
      <div className='table-desktop'>
        <Table
          columns={columns}
          dataSource={lostItems}
          pagination={false}
          scroll={{ x: '100%' }}
        />
      </div>
      <div className='table-mobile'>
        {lostItems.map((item: LostItem, index: number) => {
          console.log('item:', item);
          const report = item._report; // Access the _report object

          const planExpired = report?.planExpiry
            ? new Date(report.planExpiry) <= new Date()
            : false;

          return (
            <Card key={index} style={{ marginBottom: '16px' }}>
              <p>
                <strong>Category:</strong> {item.category}
              </p>
              <p>
                <strong>Brand:</strong> {item.brand}
              </p>
              <p>
                <strong>Model:</strong> {item.model}
              </p>
              <p>
                <strong>Color:</strong> {item.color}
              </p>
              <p>
                <strong>Payment Status:</strong>
                <Tag
                  color={
                    !report?.planExpiry
                      ? 'red'
                      : planExpired
                        ? 'orange'
                        : 'green'
                  }
                >
                  {!report?.planExpiry
                    ? 'Unpaid'
                    : planExpired
                      ? 'Expired'
                      : 'Paid'}
                </Tag>
              </p>
              <p>
                <strong>Selected Plan:</strong>{' '}
                {report?._selectedPlan
                  ? `${report._selectedPlan.title} (${report._selectedPlan?.durationInMonths} months)`
                  : 'N/A'}
              </p>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default StatusPage;
